import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { Box, Button, Typography, type ButtonProps } from '@mui/material';
import type { Action, FieldValues } from '@/api/generated/model';
import { getApiErrorMessage, getApiFieldErrors } from '@/utils/apiError';
import type { SportType } from '@/types';
import { useNotification } from '@/contexts/NotificationContext';
import { ResponsiveDialog } from '@/components/ResponsiveDialog';
import { FieldsForm, initialFieldValues } from './FieldsForm';

export type RunAction = (action: Action, values?: FieldValues) => Promise<unknown>;

const buttonProps = (action: Action): Pick<ButtonProps, 'variant' | 'color'> => {
  switch (action.style) {
    case 'primary':
      return { variant: 'contained', color: 'manage' };
    case 'danger':
      return { variant: 'outlined', color: 'error' };
    default:
      return { variant: 'outlined', color: 'manage' };
  }
};

export const actionNeedsDialog = (action: Action) => Boolean(action.confirm || action.input?.length);

const toErrors = (err: unknown): Record<string, string> => {
  const fieldErrors = getApiFieldErrors(err);
  return Object.keys(fieldErrors).length ? fieldErrors : { _: getApiErrorMessage(err) };
};

interface ActionDialogProps {
  action: Action | null;
  run: RunAction;
  onClose: () => void;
  sportType?: SportType;
}

/** Подтверждение и/или ввод значений для серверного действия. */
export const ActionDialog: React.FC<ActionDialogProps> = ({ action, run, onClose, sportType }) => {
  const [values, setValues] = useState<FieldValues>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState<Action | null>(null);

  if (action !== current) {
    setCurrent(action);
    setValues(initialFieldValues(action?.input));
    setErrors({});
  }

  if (!action) return null;

  const submit = async () => {
    setBusy(true);
    setErrors({});
    try {
      await run(action, { ...(action.params ?? {}), ...values });
      onClose();
    } catch (err) {
      setErrors(toErrors(err));
    } finally {
      setBusy(false);
    }
  };

  const hasInput = Boolean(action.input?.length);

  return (
    <ResponsiveDialog
      open
      onClose={() => !busy && onClose()}
      title={action.confirm?.title ?? action.label}
      actions={
        <>
          <Button onClick={onClose} disabled={busy} sx={{ minHeight: 48 }}>
            Отмена
          </Button>
          <Button {...buttonProps(action)} variant="contained" onClick={() => void submit()} disabled={busy} sx={{ minHeight: 48 }}>
            {action.label}
          </Button>
        </>
      }
    >
      {action.confirm?.text && (
        <Typography color="text.secondary" sx={{ mb: hasInput ? 2 : 0 }}>
          {action.confirm.text}
        </Typography>
      )}
      {hasInput && (
        <Box sx={{ pt: 1 }}>
          <FieldsForm fields={action.input ?? []} values={values} onChange={setValues} errors={errors} sportType={sportType} />
        </Box>
      )}
      {!hasInput && errors._ && (
        <Typography color="error" role="alert">
          {errors._}
        </Typography>
      )}
    </ResponsiveDialog>
  );
};

interface ActionButtonProps {
  action: Action;
  run: RunAction;
  fullWidth?: boolean;
  size?: ButtonProps['size'];
  sportType?: SportType;
  /** Подписи под кнопкой (hint, disabledReason) */
  showHint?: boolean;
  startIcon?: React.ReactNode;
}

/**
 * Кнопка серверного действия: подтверждение, ввод (`input`), причина неактивности,
 * навигация по `href`. Ошибки 422 показываются у полей.
 */
export const ActionButton: React.FC<ActionButtonProps> = ({
  action,
  run,
  fullWidth,
  size = 'large',
  sportType,
  showHint = true,
  startIcon,
}) => {
  const navigate = useNavigate();
  const { showError } = useNotification();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    if (action.href) {
      navigate(action.href);
      return;
    }
    if (actionNeedsDialog(action)) {
      setOpen(true);
      return;
    }
    setBusy(true);
    try {
      await run(action, action.params);
    } catch (err) {
      const errors = toErrors(err);
      showError(errors._ ?? Object.values(errors)[0] ?? 'Не удалось выполнить действие');
    } finally {
      setBusy(false);
    }
  };

  const caption = action.enabled ? action.hint : action.disabledReason;

  return (
    <Box sx={{ width: fullWidth ? '100%' : undefined }}>
      <Button
        {...buttonProps(action)}
        size={size}
        fullWidth={fullWidth}
        disabled={!action.enabled || busy}
        startIcon={startIcon}
        onClick={() => void handleClick()}
        sx={{ minHeight: size === 'small' ? 40 : size === 'medium' ? 44 : 48 }}
      >
        {action.label}
      </Button>
      {showHint && caption && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
          {caption}
        </Typography>
      )}
      <ActionDialog action={open ? action : null} run={run} onClose={() => setOpen(false)} sportType={sportType} />
    </Box>
  );
};
